import { FFEScheduleItem, ProductInterface } from "ts-interfaces";
import { DEFAULT_SECTIONS } from "../scheduleConstants";

export type ProductsMap = Record<string, ProductInterface>;

// Hydration map keys are source-qualified — the same refId may exist in both
// productsV2 and userProducts.
export const productKey = (item: FFEScheduleItem): string =>
  `${item.source}:${item.refId}`;

export const productForItem = (
  item: FFEScheduleItem,
  products: ProductsMap | undefined,
): ProductInterface | undefined => products?.[productKey(item)];

// The unsaved custom-product draft lives outside schedule.items until Done,
// so every itemId lookup the expand dialog can reach resolves it FIRST.
// Store-free (reads only these two fields) so row hooks, modals and their
// tests don't depend on the store module.
/** Writes an unsaved product the details editor finished with; resolves to the saved doc. */
export type PersistProduct = (
  product: ProductInterface,
) => Promise<ProductInterface>;
export interface CustomProductDraft {
  item: FFEScheduleItem;
  product: ProductInterface;
  /** PBR material: the upload → maps → doc write Done runs instead of a plain save. */
  persist?: PersistProduct;
  /** Started from the Custom Product picker: the details dialog offers Back to it. */
  returnToPicker?: boolean;
}
export interface DraftLookup {
  customProductDraft: CustomProductDraft | null;
  schedule: { items: FFEScheduleItem[]; products?: ProductsMap } | null;
}
export const draftFor = (
  s: DraftLookup,
  itemId: string | null | undefined,
): CustomProductDraft | null =>
  itemId && s.customProductDraft?.item.itemId === itemId
    ? s.customProductDraft
    : null;
export const resolveItem = (
  s: DraftLookup,
  itemId: string | null | undefined,
): FFEScheduleItem | undefined =>
  draftFor(s, itemId)?.item ??
  (itemId ? s.schedule?.items.find((i) => i.itemId === itemId) : undefined);
// `item` may be passed when the caller already resolved it (one scan, not two).
export const resolveProduct = (
  s: DraftLookup,
  itemId: string | null | undefined,
  item: FFEScheduleItem | undefined = resolveItem(s, itemId),
): ProductInterface | undefined =>
  draftFor(s, itemId)?.product ??
  (item ? productForItem(item, s.schedule?.products) : undefined);

// The product's static catalog category (first entry if it's an array). Only a
// suggestion source for the Section picker — it never drives grouping.
export const firstCategory = (
  product?: ProductInterface,
): string | undefined => {
  const category = product?.category as unknown;
  if (Array.isArray(category)) return category[0] || undefined;
  if (typeof category === "string" && category) return category;
  return undefined;
};

export const NO_CATEGORY = "Uncategorized";

// True when a user-typed Section name collides with the derived bucket's
// sentinel. Such names are treated as "no Section" at every write chokepoint —
// never declared, never stored as a literal override — because a literal
// "Uncategorized" override would merge with the derived bucket behind
// identical UI while persisting a divergent state.
export const isNoCategoryName = (name: string | undefined): boolean =>
  !!name && name.trim().toLowerCase() === NO_CATEGORY.toLowerCase();

// Append `name` to the declared Section list if it's new; `afterKey` inserts
// it right after that declared Section instead. `undefined`/blank is a no-op
// (clearing a Section declares nothing). Shared by every path that stamps a
// Section (assign, bulk move, per-section adds) so a stamped Section always
// persists in `sections[]` like one made with "+ Create Section".
export const declareSection = (
  sections: string[] | undefined,
  name: string | undefined,
  afterKey?: string,
): string[] | undefined => {
  if (!name || (sections || []).includes(name)) return sections;
  const existing = sections || [];
  const at = afterKey ? existing.indexOf(afterKey) : -1;
  return at >= 0
    ? [...existing.slice(0, at + 1), name, ...existing.slice(at + 1)]
    : [...existing, name];
};

// The single grouping key. A section only exists if the user set one
// (item.categoryOverride); rows without one group under "Uncategorized".
export function groupKeyForItem(item: FFEScheduleItem): string {
  return item.categoryOverride || NO_CATEGORY;
}

export interface ScheduleGroup {
  key: string;
  items: FFEScheduleItem[];
}

// Items ordered by item.order; buckets emitted in first-appearance order so
// manual reordering also reorders groups predictably.
//
// `sections` (the user's declared Section list) seed buckets FIRST, in order,
// so an empty Section a user created still renders and keeps a stable
// position. Every other bucket — a Section override or "Uncategorized" —
// follows in first-appearance order.
export function groupItems(
  items: FFEScheduleItem[],
  sections?: string[],
): ScheduleGroup[] {
  const sorted = [...items].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const buckets = new Map<string, FFEScheduleItem[]>();
  // Seed declared sections in order (empties included).
  for (const s of sections || []) if (s && !buckets.has(s)) buckets.set(s, []);
  for (const item of sorted) {
    const key = groupKeyForItem(item);
    const bucket = buckets.get(key);
    if (bucket) bucket.push(item);
    else buckets.set(key, [item]);
  }
  return [...buckets.entries()].map(([key, groupItems]) => ({
    key,
    items: groupItems,
  }));
}

// Session-local search over product name, brand (supplier), and item code.
export function filterItems(
  items: FFEScheduleItem[],
  products: ProductsMap | undefined,
  query: string,
): FFEScheduleItem[] {
  const q = query.trim().toLowerCase();
  if (!q) return items;
  return items.filter((item) => {
    const product = productForItem(item, products);
    return (
      (product?.name || "").toLowerCase().includes(q) ||
      (product?.supplier || "").toLowerCase().includes(q) ||
      (item.itemCode || "").toLowerCase().includes(q)
    );
  });
}

// Section suggestion list for the inline picker: the user's declared Sections,
// the Sections actually in use (distinct item.categoryOverride), the product
// categories currently in use (so a row can be pinned to an existing category
// group), then the DEFAULT_SECTIONS presets.
export function sectionOptions(
  items: FFEScheduleItem[],
  products?: ProductsMap,
  sections?: string[],
): string[] {
  const seen = new Set<string>();
  const known: string[] = [];
  const add = (s: string | undefined) => {
    // The sentinel never appears as a pickable Section — the pickers offer
    // their own "Uncategorized" affordance that CLEARS the override instead.
    if (s && !seen.has(s) && !isNoCategoryName(s)) {
      seen.add(s);
      known.push(s);
    }
  };
  // Declared sections first (empties included), then in-use overrides, then
  // in-use product categories, then presets.
  for (const s of sections || []) add(s);
  for (const item of items) add(item.categoryOverride);
  for (const item of items) add(firstCategory(productForItem(item, products)));
  const presets = DEFAULT_SECTIONS.filter((s) => !seen.has(s));
  return [...known, ...presets];
}
